import React, { useEffect, useMemo, useRef, useState } from 'react';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';

import Axios from '../../../networking/intraceptor';
import { useCustomDispatch } from '../../../hooks';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import { loginSuccess } from '../../../services/actions/auth/authActions';

const getDateParts = (dateValue = '') => {
	const [year = '', month = '', day = ''] = String(dateValue).split('-');

	return { day, month, year };
};

const onlyDigits = (value, maxLength) => String(value).replace(/\D/g, '').slice(0, maxLength);

const monthOptions = [
	{ value: '01', label: 'January' },
	{ value: '02', label: 'February' },
	{ value: '03', label: 'March' },
	{ value: '04', label: 'April' },
	{ value: '05', label: 'May' },
	{ value: '06', label: 'June' },
	{ value: '07', label: 'July' },
	{ value: '08', label: 'August' },
	{ value: '09', label: 'September' },
	{ value: '10', label: 'October' },
	{ value: '11', label: 'November' },
	{ value: '12', label: 'December' },
];

const buildDateString = ({ day, month, year }) => {
	const normalizedDay = onlyDigits(day, 2);
	const normalizedMonth = onlyDigits(month, 2);
	const normalizedYear = onlyDigits(year, 4);

	if (!normalizedDay || !normalizedMonth || !normalizedYear) return '';
	if (normalizedYear.length !== 4) return '';

	const dayNumber = Number(normalizedDay);
	const monthNumber = Number(normalizedMonth);
	const yearNumber = Number(normalizedYear);

	if (!dayNumber || !monthNumber || monthNumber > 12) return '';

	const candidate = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber, 12));
	const isValidDate =
		candidate.getUTCFullYear() === yearNumber
		&& candidate.getUTCMonth() === monthNumber - 1
		&& candidate.getUTCDate() === dayNumber;

	if (!isValidDate) return '';

	return [
		normalizedYear,
		String(monthNumber).padStart(2, '0'),
		String(dayNumber).padStart(2, '0'),
	].join('-');
};

const DeliveryStartDateSetupModal = () => {
	const dispatch = useCustomDispatch();
	const { authData, adminModule, pharmacyId, pharmacyLoginData } = useCustomSelectors();
	const authDataRef = useRef(authData);

	const initialSetup = pharmacyLoginData?.delivery_start_date_setup;
	const isAdmin = Number(adminModule) === 1;

	const setupKey = useMemo(() => {
		if (!pharmacyId) {
			return null;
		}

		return `${pharmacyId}:${initialSetup?.delivery_start_date || 'missing'}`;
	}, [initialSetup?.delivery_start_date, pharmacyId]);

	const [setup, setSetup] = useState(initialSetup || null);
	const [initializedSetupKey, setInitializedSetupKey] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [deliveryDay, setDeliveryDay] = useState('');
	const [deliveryMonth, setDeliveryMonth] = useState('');
	const [deliveryYear, setDeliveryYear] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fieldError, setFieldError] = useState('');
	const [submitError, setSubmitError] = useState('');

	const shouldPrompt = isAdmin && Boolean(setup?.requires_prompt);

	useEffect(() => {
		authDataRef.current = authData;
	}, [authData]);

	useEffect(() => {
		setSetup(initialSetup || null);
	}, [initialSetup]);

	useEffect(() => {
		if (!isAdmin || !pharmacyId) {
			setIsOpen(false);
			return undefined;
		}

		let cancelled = false;

		const loadContext = async () => {
			try {
				const response = await Axios.get('delivery-start-date-context');
				if (cancelled) {
					return;
				}

				const nextSetup = response?.data?.data?.delivery_start_date_setup || null;
				setSetup(nextSetup);

				const latestAuthData = authDataRef.current;
				if (latestAuthData?.data && nextSetup) {
					dispatch(
						loginSuccess({
							...latestAuthData.data,
							pharmacy: (latestAuthData.data.pharmacy || []).map((pharmacy) =>
								pharmacy.id === pharmacyId
									? {
											...pharmacy,
											delivery_start_date: nextSetup.delivery_start_date,
											delivery_start_date_setup: nextSetup,
									  }
									: pharmacy
							),
						})
					);
				}
			} catch (error) {
				if (!cancelled) {
					setSubmitError(
						error?.response?.data?.message ||
							'Unable to load delivery start date setup right now.'
					);
				}
			}
		};

		loadContext();

		return () => {
			cancelled = true;
		};
	}, [dispatch, isAdmin, pharmacyId]);

	useEffect(() => {
		if (!shouldPrompt || !setupKey || initializedSetupKey === setupKey) {
			if (!shouldPrompt) {
				setIsOpen(false);
			}
			return;
		}

		const nextDeliveryStartDate = setup?.delivery_start_date || '';
		const nextDateParts = getDateParts(nextDeliveryStartDate);

		setDeliveryDay(nextDateParts.day);
		setDeliveryMonth(nextDateParts.month);
		setDeliveryYear(nextDateParts.year);
		setFieldError('');
		setSubmitError('');
		setIsOpen(true);
		setInitializedSetupKey(setupKey);
	}, [initializedSetupKey, setup?.delivery_start_date, setupKey, shouldPrompt]);

	useEffect(() => {
		if (!isOpen) return undefined;

		const { overflow } = document.body.style;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = overflow;
		};
	}, [isOpen]);

	const applySetup = (nextSetup) => {
		setSetup(nextSetup);

		if (!authData?.data || !nextSetup) {
			return;
		}

		dispatch(
			loginSuccess({
				...authData.data,
				pharmacy: (authData.data.pharmacy || []).map((pharmacy) =>
					pharmacy.id === pharmacyId
						? {
								...pharmacy,
								delivery_start_date: nextSetup.delivery_start_date,
								delivery_start_date_setup: nextSetup,
						  }
						: pharmacy
				),
			})
		);
	};

	const handleDatePartChange = (part, value) => {
		const normalizedValue = onlyDigits(value, part === 'year' ? 4 : 2);
		const nextDateParts = {
			day: deliveryDay,
			month: deliveryMonth,
			year: deliveryYear,
			[part]: normalizedValue,
		};

		if (part === 'day') setDeliveryDay(normalizedValue);
		if (part === 'month') setDeliveryMonth(normalizedValue);
		if (part === 'year') setDeliveryYear(normalizedValue);

		setFieldError('');
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setSubmitError('');

		const selectedDeliveryStartDate = buildDateString({
			day: deliveryDay,
			month: deliveryMonth,
			year: deliveryYear,
		});

		if (!selectedDeliveryStartDate) {
			setFieldError('Enter a valid delivery start date.');
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await Axios.post('delivery-start-date', {
				delivery_start_date: selectedDeliveryStartDate,
			});

			const nextSetup =
				response?.data?.data?.delivery_start_date_setup || {
					requires_prompt: false,
					delivery_start_date: selectedDeliveryStartDate,
					has_completed_deliveries: false,
					is_admin: true,
				};

			applySetup(nextSetup);
			setIsOpen(false);
		} catch (error) {
			const responseErrors = error?.response?.data?.errors || {};
			setFieldError(responseErrors?.delivery_start_date?.[0] || '');
			setSubmitError(
				error?.response?.data?.message ||
					'Unable to save the delivery start date right now. Please try again.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-[156] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
			<div className="relative w-full max-w-xl overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#f8fbff_55%,#f8fafc_100%)] p-6 shadow-[0_32px_120px_rgba(15,23,42,0.24)]">
				<div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.18),transparent_48%),radial-gradient(circle_at_top_right,rgba(14,165,233,0.12),transparent_36%)]" />
				<form onSubmit={handleSubmit} className="relative">
					<div className="flex items-center gap-4">
						<span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
							<EventAvailableOutlinedIcon />
						</span>
						<div className="flex min-h-12 flex-1 items-center">
							<h2 className="text-2xl font-semibold text-slate-900">
								Delivery start date
							</h2>
						</div>
					</div>

					<div className="mt-7 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
						
						<div className="grid grid-cols-3 gap-3">
							<div>
								<label
									htmlFor="delivery-start-day"
									className="mb-2 block text-xs font-semibold text-slate-600"
								>
									Date
								</label>
								<input
									id="delivery-start-day"
									type="text"
									inputMode="numeric"
									value={deliveryDay}
									onChange={(event) => handleDatePartChange('day', event.target.value)}
									disabled={isSubmitting}
									placeholder="DD"
									className={`h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition ${
										fieldError
											? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
											: 'border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
									} ${isSubmitting ? 'cursor-not-allowed bg-slate-100' : ''}`}
								/>
							</div>
							<div>
								<label
									htmlFor="delivery-start-month"
									className="mb-2 block text-xs font-semibold text-slate-600"
								>
									Month
								</label>
								<select
									id="delivery-start-month"
									value={deliveryMonth}
									onChange={(event) => handleDatePartChange('month', event.target.value)}
									disabled={isSubmitting}
									className={`h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition ${
										fieldError
											? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
											: 'border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
									} ${isSubmitting ? 'cursor-not-allowed bg-slate-100' : ''}`}
								>
									<option value="">Month</option>
									{monthOptions.map((month) => (
										<option key={month.value} value={month.value}>
											{month.label}
										</option>
									))}
								</select>
							</div>
							<div>
								<label
									htmlFor="delivery-start-year"
									className="mb-2 block text-xs font-semibold text-slate-600"
								>
									Year
								</label>
								<input
									id="delivery-start-year"
									type="text"
									inputMode="numeric"
									value={deliveryYear}
									onChange={(event) => handleDatePartChange('year', event.target.value)}
									disabled={isSubmitting}
									placeholder="YYYY"
									className={`h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition ${
										fieldError
											? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
											: 'border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
									} ${isSubmitting ? 'cursor-not-allowed bg-slate-100' : ''}`}
								/>
							</div>
						</div>
						{fieldError && <p className="mt-2 text-sm text-red-600">{fieldError}</p>}
					</div>

					{submitError && <p className="mt-5 text-sm text-red-600">{submitError}</p>}

					<div className="mt-6 flex items-center justify-end">
						<button
							type="submit"
							disabled={isSubmitting}
							className={`rounded-xl border px-5 py-3 text-sm font-semibold transition ${
								isSubmitting
									? 'cursor-not-allowed border-slate-300 bg-slate-200 text-slate-600'
									: 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700'
							}`}
						>
							{isSubmitting ? 'Saving...' : 'Submit'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default DeliveryStartDateSetupModal;
